import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "reactstrap";
import { formatCurrency } from "utils/currency";
import {
  getPaymentExpectationSummary,
  type PaymentExpectationSummaryGroup,
} from "../services/PaymentExpectationService";

interface SummaryCardProps {
  title: string;
  count: number;
  totalBalance: number;
  iconClass: string;
  colorClass: string;
  bgClass: string;
}

function SummaryCard({
  title,
  count,
  totalBalance,
  iconClass,
  colorClass,
  bgClass,
}: SummaryCardProps) {
  return (
    <div className={`card card-animate border-0 shadow-none ${bgClass}`}>
      <div className="card-body">
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <p className="text-uppercase fw-medium text-muted mb-2 small">{title}</p>
            <h4 className={`mb-0 ${colorClass}`}>{formatCurrency(totalBalance, { compact: true })}</h4>
            <p className="text-muted mb-0 mt-1 small">
              {count} expectation{count !== 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div
              className={`avatar-sm rounded-circle ${colorClass.replace("text-", "bg-").replace("text-", "bg-")}-subtle d-flex align-items-center justify-content-center`}
              style={{ width: 48, height: 48 }}
            >
              <i className={`${iconClass} fs-22 ${colorClass}`} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function PaymentExpectationSummaryWidgets({ refreshSignal = 0 }: { refreshSignal?: number }) {
  const [data, setData] = useState<{
    upcomingNext7Days: PaymentExpectationSummaryGroup;
    upcomingNext30Days: PaymentExpectationSummaryGroup;
    overdue: PaymentExpectationSummaryGroup;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPaymentExpectationSummary()
      .then((res) => {
        if (!cancelled && res.success) setData(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [refreshSignal]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-3">
        <Spinner size="sm" color="primary" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <Row className="mb-3 g-3">
      <Col md={4}>
        <SummaryCard
          title="Due in next 7 days"
          count={data.upcomingNext7Days.count}
          totalBalance={data.upcomingNext7Days.totalBalance}
          iconClass="ri-calendar-event-line"
          colorClass="text-primary"
          bgClass="bg-primary-subtle"
        />
      </Col>
      <Col md={4}>
        <SummaryCard
          title="Due in next 30 days"
          count={data.upcomingNext30Days.count}
          totalBalance={data.upcomingNext30Days.totalBalance}
          iconClass="ri-calendar-line"
          colorClass="text-info"
          bgClass="bg-info-subtle"
        />
      </Col>
      <Col md={4}>
        <SummaryCard
          title="Overdue"
          count={data.overdue.count}
          totalBalance={data.overdue.totalBalance}
          iconClass="ri-alarm-warning-line"
          colorClass="text-danger"
          bgClass="bg-danger-subtle"
        />
      </Col>
    </Row>
  );
}
